import type { CompanyPricingRepository } from "../../../domain/repositories/company/company-pricing-repository";
import { companyPricingApiService } from "../../api/company/company-pricing-api-service";
import type {
  WasteRateSetting,
  WasteRateSettingRequest,
  ShippingFeeSetting,
  ShippingFeeSettingRequest,
  EventDiscount,
  EventDiscountRequest,
} from "../../../domain/types/pricing";

export class CompanyPricingRepositoryImpl implements CompanyPricingRepository {
  // ========== 손지율 비용 관리 ==========

  async getWasteRates(companyId: number): Promise<WasteRateSetting[]> {
    const response = await companyPricingApiService.getWasteRates(companyId);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "손지율 설정을 가져올 수 없습니다.");
  }

  async createWasteRate(
    companyId: number,
    request: WasteRateSettingRequest,
  ): Promise<WasteRateSetting> {
    const response = await companyPricingApiService.createWasteRate(
      companyId,
      request,
    );
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "손지율 설정 생성에 실패했습니다.");
  }

  async updateWasteRate(
    companyId: number,
    wasteRateId: number,
    request: WasteRateSettingRequest,
  ): Promise<WasteRateSetting> {
    const response = await companyPricingApiService.updateWasteRate(
      companyId,
      wasteRateId,
      request,
    );
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "손지율 설정 수정에 실패했습니다.");
  }

  async deleteWasteRate(
    companyId: number,
    wasteRateId: number,
  ): Promise<boolean> {
    const response = await companyPricingApiService.deleteWasteRate(
      companyId,
      wasteRateId,
    );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error(response.errorMsg || "손지율 설정 삭제에 실패했습니다.");
  }

  // ========== 배송비 관리 ==========

  async getShippingFee(companyId: number): Promise<ShippingFeeSetting | null> {
    const response = await companyPricingApiService.getShippingFee(companyId);
    // 배송비 설정이 없는 경우 null 반환 (정상 케이스)
    return response.data ?? null;
  }

  async updateShippingFee(
    companyId: number,
    request: ShippingFeeSettingRequest,
  ): Promise<ShippingFeeSetting> {
    const response = await companyPricingApiService.updateShippingFee(
      companyId,
      request,
    );
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "배송비 설정 저장에 실패했습니다.");
  }

  // ========== 이벤트 할인 관리 ==========

  async getEventDiscounts(companyId: number): Promise<EventDiscount[]> {
    const response = await companyPricingApiService.getEventDiscounts(companyId);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "이벤트 할인 목록을 가져올 수 없습니다.");
  }

  async createEventDiscount(
    companyId: number,
    request: EventDiscountRequest,
  ): Promise<EventDiscount> {
    const response = await companyPricingApiService.createEventDiscount(
      companyId,
      request,
    );
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "이벤트 할인 생성에 실패했습니다.");
  }

  async updateEventDiscount(
    companyId: number,
    eventDiscountId: number,
    request: EventDiscountRequest,
  ): Promise<EventDiscount> {
    const response = await companyPricingApiService.updateEventDiscount(
      companyId,
      eventDiscountId,
      request,
    );
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "이벤트 할인 수정에 실패했습니다.");
  }

  async deleteEventDiscount(
    companyId: number,
    eventDiscountId: number,
  ): Promise<boolean> {
    const response = await companyPricingApiService.deleteEventDiscount(
      companyId,
      eventDiscountId,
    );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error(response.errorMsg || "이벤트 할인 삭제에 실패했습니다.");
  }
}
